import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { trigger, transition, style, animate } from '@angular/animations';
import { ButtonModule } from 'primeng/button';
import type { BannerConfig } from '@shared/models/banner.model';

/**
 * Banner Component
 * Presentational component for displaying a single banner notification
 *
 * @description
 * Displays a banner with icon, title, message, and optional close button.
 * Features smooth slide-in animation from the right side.
 *
 * Features:
 * - Type-based styling (success, error, warning, info)
 * - Custom icon support
 * - Smooth slide-in animation
 * - Accessibility (ARIA attributes)
 * - Manual dismiss with close button
 *
 * @example
 * ```html
 * <app-banner
 *   [config]="bannerConfig"
 *   (onClose)="handleClose()"
 * />
 * ```
 */
@Component({
  selector: 'app-banner',
  standalone: true,
  imports: [CommonModule, ButtonModule],
  templateUrl: './banner.component.html',
  styleUrl: './banner.component.css',
  animations: [
    trigger('slideInFromRight', [
      transition(':enter', [
        style({
          transform: 'translateX(100%)',
          opacity: 0,
        }),
        animate(
          '300ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({
            transform: 'translateX(0)',
            opacity: 1,
          }),
        ),
      ]),
      transition(':leave', [
        animate(
          '200ms cubic-bezier(0.4, 0, 1, 1)',
          style({
            transform: 'translateX(100%)',
            opacity: 0,
          }),
        ),
      ]),
    ]),
  ],
})
export class BannerComponent {
  /**
   * Banner configuration
   */
  @Input({ required: true }) config!: BannerConfig;

  /**
   * Close event emitter
   */
  @Output() onClose = new EventEmitter<void>();

  /**
   * Handle close button click
   */
  handleClose(): void {
    this.onClose.emit();
  }
}
